import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Skip the call entirely if we have no token AND no session cookie evidence
    const hasToken = !!localStorage.getItem("orixa_token");
    if (!hasToken && !document.cookie.includes("session_token")) {
      setUser(null);
      setOrg(null);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setOrg(data.organization);
      return data;
    } catch (e) {
      setUser(null);
      setOrg(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip /auth/me; AuthCallback handles it
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("orixa_token", data.access_token);
    setUser(data.user);
    setOrg(data.organization);
    return data;
  };

  const signup = async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    localStorage.setItem("orixa_token", data.access_token);
    setUser(data.user);
    setOrg(data.organization);
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* logout is best-effort, cookie may already be cleared */ }
    localStorage.removeItem("orixa_token");
    setUser(null);
    setOrg(null);
  };

  return (
    <AuthCtx.Provider value={{ user, org, loading, login, signup, logout, refresh, setOrg }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
